import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, MapPin, Clock, ArrowRight, Users, Heart, TrendingUp, GraduationCap } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useLanguage } from '../context/LanguageContext';
import { jobs } from '../data/mockData';

const iconMap = {
  Users: Users,
  TrendingUp: TrendingUp,
  GraduationCap: GraduationCap,
  Heart: Heart
};

const Careers = () => {
  const { currentLang, t, isRTL } = useLanguage();
  const jobList = jobs[currentLang] || jobs.fr;

  return (
    <div className="min-h-screen">
      <Header />
      <main dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Hero Banner */}
        <section className="pt-32 pb-20 bg-gradient-to-br from-[#e8d4e5] via-[#d9c5d6] to-[#c5a3c0]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                {t.careers.title}
              </h1>
              <p className="text-lg text-gray-700 leading-relaxed">
                {t.careers.description}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Why Join Us */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">{t.careers.whyJoin}</h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {t.careers.benefits.map((benefit, index) => {
                const IconComponent = iconMap[benefit.icon];
                return (
                  <motion.div
                    key={benefit.title}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="text-center"
                  >
                    <div className="w-16 h-16 bg-[#9B4B8C]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-8 h-8 text-[#9B4B8C]" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                    <p className="text-gray-600 text-sm">{benefit.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Job Openings */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">{t.careers.jobsTitle}</h2>
            </motion.div>

            <div className="space-y-6">
              {jobList.map((job, index) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow"
                >
                  <div className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 ${isRTL ? 'lg:flex-row-reverse' : ''}`}>
                    <div className="flex-1">
                      <div className={`flex items-center gap-3 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <span className="px-3 py-1 bg-[#9B4B8C]/10 text-[#9B4B8C] text-xs font-medium rounded-full">
                          {job.department}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{job.title}</h3>
                      <p className="text-gray-600 mb-4">{job.description}</p>
                      <div className={`flex flex-wrap gap-4 text-sm text-gray-500 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <MapPin className="w-4 h-4" />
                          {job.location}
                        </div>
                        <div className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <Clock className="w-4 h-4" />
                          {job.type}
                        </div>
                      </div>
                    </div>
                    <button className={`inline-flex items-center gap-2 bg-[#9B4B8C] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#7d3c71] transition-colors whitespace-nowrap ${isRTL ? 'flex-row-reverse' : ''}`}>
                      {t.careers.applyButton}
                      <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Spontaneous Application */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-12 bg-[#9B4B8C] rounded-2xl p-8 text-center text-white"
            >
              <Briefcase className="w-12 h-12 mx-auto mb-4 opacity-80" />
              <h3 className="text-2xl font-bold mb-4">{t.careers.spontaneous.title}</h3>
              <p className="text-white/80 max-w-2xl mx-auto mb-6">
                {t.careers.spontaneous.description}
              </p>
              <button className={`inline-flex items-center gap-2 bg-white text-[#9B4B8C] px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}>
                {t.careers.spontaneous.button}
                <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
              </button>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Careers;
